import React from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Server, Trash2, Mail, Lock, Eye } from 'lucide-react';

interface LegalSection {
  heading: string;
  paragraphs?: string[];
  paragraphsAfter?: string[];
  items?: string[];
  note?: string;
  muted?: boolean;
  highlightEmail?: string;
  contactEmail?: string;
}

const HEADING_ICONS: Record<number, React.ReactNode> = {
  0: <Mail className="h-4 w-4 text-primary" />,
  1: <Eye className="h-4 w-4 text-primary" />,
  3: <Server className="h-4 w-4 text-primary" />,
  6: <Lock className="h-4 w-4 text-primary" />,
  7: <Trash2 className="h-4 w-4 text-destructive" />,
};

const PolitiqueConfidentialitePage = () => {
  const { t, i18n } = useTranslation('legal');
  const sections = t('privacy.sections', { returnObjects: true }) as LegalSection[];

  return (
    <MainLayout>
      <div className="container mx-auto py-8 px-4">
        <h1 className="text-3xl font-bold text-center mb-8">{t('privacy.pageTitle')}</h1>

        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              {t('privacy.cardTitle')}
            </CardTitle>
            <p className="text-sm text-muted-foreground">{t('privacy.lastUpdate')}</p>
          </CardHeader>
          <CardContent className="space-y-8">
            {sections.map((section, index) => (
              <section
                key={index}
                className={section.muted ? 'bg-muted/30 p-6 rounded-lg' : undefined}
              >
                <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                  {HEADING_ICONS[index]}
                  {section.heading}
                </h2>

                {section.paragraphs?.map((paragraph, pIndex) => (
                  <p
                    key={pIndex}
                    className={`text-muted-foreground ${pIndex < (section.paragraphs?.length ?? 0) - 1 || section.items ? 'mb-2' : ''}`}
                    dangerouslySetInnerHTML={{ __html: paragraph }}
                  />
                ))}

                {section.highlightEmail && !section.paragraphsAfter && (
                  <p className="font-medium mt-2 text-primary">{section.highlightEmail}</p>
                )}

                {section.items && (
                  <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2 mt-2">
                    {section.items.map((item, iIndex) => (
                      <li key={iIndex} dangerouslySetInnerHTML={{ __html: item }} />
                    ))}
                  </ul>
                )}

                {section.note && (
                  <p
                    className="text-sm text-muted-foreground mt-3 bg-muted/50 p-3 rounded-lg"
                    dangerouslySetInnerHTML={{ __html: section.note }}
                  />
                )}

                {section.paragraphsAfter?.map((paragraph, pIndex) => (
                  <p
                    key={pIndex}
                    className="text-muted-foreground mt-3"
                    dangerouslySetInnerHTML={{ __html: paragraph }}
                  />
                ))}

                {section.contactEmail && (
                  <p className="font-medium mt-2">{section.contactEmail}</p>
                )}

                {section.highlightEmail && section.paragraphsAfter && (
                  <p className="font-medium mt-1 text-primary">{section.highlightEmail}</p>
                )}
              </section>
            ))}

            {!i18n.language.startsWith('fr') && (
              <p className="text-xs text-muted-foreground">{t('prevailNotice')}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default PolitiqueConfidentialitePage;
